import React, { useEffect, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiLoader,
  FiRefreshCw,
  FiTrendingDown,
  FiZap,
} from 'react-icons/fi';

import { Textarea } from '@/shared/components/ui';

import type {
  AgentQualitySnapshotResponse,
  PromptSuggestion,
  SnapshotRegressionInfo,
} from '../../services/agentTrainingApi';
import {
  getMetricDescription,
  getMetricLabel,
  QUALITY_TEST_METRICS,
} from '../../utils/metric-labels';

interface TrainStep3Props {
  snapshot: AgentQualitySnapshotResponse;
  suggestions: PromptSuggestion[];
  loadingSuggestions: boolean;
  suggestionsError: Error | null;
  selectedSuggestionIds: string[];
  userInstructions: string;
  onToggleSuggestion: (id: string) => void;
  onUserInstructionsChange: (value: string) => void;
  onGenerateSuggestions: () => void;
  onApply: () => void;
  applying: boolean;
  /**
   * Erro retornado quando a geração do novo prompt falhou (ex: timeout
   * do LLM no backend). Fica no Step 3 pra cliente tentar de novo.
   */
  applyError: string | null;
  /**
   * Texto do novo prompt sendo gerado em tempo real via streaming SSE.
   * Acumula conforme chunks chegam do backend. Mostrado num painel ao
   * vivo enquanto `isStreamingApply` é true.
   */
  streamingPrompt: string;
  /** True enquanto o stream SSE está ativo (apply em progresso). */
  isStreamingApply: boolean;
}

// Labels + descrições centralizados em ../../utils/metric-labels.ts —
// a constante `QUALITY_TEST_METRICS` lista as métricas que SEMPRE
// pontuam em snapshot de quality test (Tier 1 + Tier 2).

const PRIORITY_CONFIG: Record<
  PromptSuggestion['priority'],
  { label: string; color: string }
> = {
  high: { label: 'Alta', color: '#ef4444' },
  medium: { label: 'Média', color: '#f59e0b' },
  low: { label: 'Baixa', color: '#8b5cf6' },
};

/**
 * Step 3 — Resultados do teste + sugestões do Prompt Coach + instruções
 * livres. O componente mais denso do fluxo.
 *
 * Três seções:
 * 1. **Resumo das métricas** — tabela compacta de 8 scores
 * 2. **Sugestões do Prompt Coach** — lista selecionável (checkboxes)
 * 3. **Instruções adicionais** — textarea livre
 *
 * O botão "Aplicar e ver diff" fica habilitado quando pelo menos uma
 * sugestão está selecionada OU há instruções livres.
 */
export const TrainStep3_Results: React.FC<TrainStep3Props> = ({
  snapshot,
  suggestions,
  loadingSuggestions,
  suggestionsError,
  selectedSuggestionIds,
  userInstructions,
  onToggleSuggestion,
  onUserInstructionsChange,
  onGenerateSuggestions,
  onApply,
  applying,
  applyError,
  streamingPrompt,
  isStreamingApply,
}) => {
  const [hasTriggeredSuggestions, setHasTriggeredSuggestions] = useState(false);
  const streamingBoxRef = useRef<HTMLDivElement>(null);

  // Auto-scroll do preview de streaming pra sempre mostrar a última linha
  useEffect(() => {
    if (isStreamingApply && streamingBoxRef.current) {
      streamingBoxRef.current.scrollTop = streamingBoxRef.current.scrollHeight;
    }
  }, [isStreamingApply, streamingPrompt]);

  // Auto-dispara geração de sugestões quando o componente monta (única vez).
  // Skipa em modo manual (snapshot.scenarios vazio).
  useEffect(() => {
    if (
      !hasTriggeredSuggestions &&
      snapshot.scenarios.length > 0 &&
      suggestions.length === 0 &&
      !loadingSuggestions
    ) {
      onGenerateSuggestions();
      setHasTriggeredSuggestions(true);
    }
  }, [
    hasTriggeredSuggestions,
    snapshot.scenarios.length,
    suggestions.length,
    loadingSuggestions,
    onGenerateSuggestions,
  ]);

  const canApply =
    selectedSuggestionIds.length > 0 || userInstructions.trim().length > 0;

  return (
    <div className="space-y-5">
      {/* Banner de regressão detectada (Coach v2) */}
      {snapshot.regressionInfo && (
        <RegressionBanner info={snapshot.regressionInfo} />
      )}

      {/* Resumo das métricas (só se tem scenarios) */}
      {snapshot.scenarios.length > 0 && snapshot.summary && (
        <MetricsSummary summary={snapshot.summary} />
      )}

      {/* Sugestões do Prompt Coach */}
      {snapshot.scenarios.length > 0 && (
        <SuggestionsList
          suggestions={suggestions}
          loading={loadingSuggestions}
          error={suggestionsError}
          selectedIds={selectedSuggestionIds}
          onToggle={onToggleSuggestion}
          onRegenerate={onGenerateSuggestions}
        />
      )}

      {/* Campo de instruções livres */}
      <div>
        <label
          className="text-sm font-semibold text-slate-800 mb-2 block"
        >
          Instruções adicionais{' '}
          <span className="text-xs font-normal text-slate-500">
            (opcional se houver sugestões selecionadas)
          </span>
        </label>
        <p className="text-xs text-slate-500 mb-2">
          Escreva qualquer ajuste que você queira fazer no prompt do agente.
          Por exemplo: "adicionar regra sobre feriados", "usar tom mais formal",
          "sempre responder em no máximo 3 frases".
        </p>
        <Textarea
          value={userInstructions}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onUserInstructionsChange(e.target.value)
          }
          rows={5}
          placeholder="Escreva suas instruções livres aqui..."
        />
        {/* Aviso quando Coach não gerou sugestões — usuário fica preso sem
            saber que precisa preencher esta textarea pra avançar. */}
        {suggestions.length === 0 &&
          !loadingSuggestions &&
          snapshot.scenarios.length > 0 && (
            <p className="text-xs mt-2" style={{ color: '#92400e' }}>
              O Coach não gerou sugestões automaticamente. Para aplicar
              mudanças, escreva suas instruções acima.
            </p>
          )}
      </div>

      {/* Painel de streaming — mostra o novo prompt sendo gerado em tempo real.
          Fica visível enquanto o SSE está ativo OU enquanto temos texto
          mas o apply ainda não finalizou (transição curta). */}
      {(isStreamingApply || (streamingPrompt && applying)) && (
        <div
          className="rounded-lg border p-4"
          style={{
            backgroundColor: 'rgba(139, 92, 246, 0.04)',
            borderColor: 'rgba(139, 92, 246, 0.25)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <FiZap
              className="h-4 w-4 animate-pulse"
              style={{ color: '#8b5cf6' }}
            />
            <span className="text-sm font-semibold text-slate-800">
              Gerando o novo prompt em tempo real...
            </span>
            <span className="text-xs text-slate-500 ml-auto tabular-nums">
              {streamingPrompt.length.toLocaleString('pt-BR')} caracteres
            </span>
          </div>
          <div
            ref={streamingBoxRef}
            className="text-xs font-mono whitespace-pre-wrap rounded p-3 max-h-64 overflow-y-auto leading-relaxed"
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              color: '#334155',
            }}
          >
            {streamingPrompt ? (
              <>
                {streamingPrompt}
                <span
                  className="inline-block w-1.5 h-3.5 ml-0.5 align-middle animate-pulse"
                  style={{ backgroundColor: '#8b5cf6' }}
                />
              </>
            ) : (
              <span className="text-slate-400 italic">
                Aguardando resposta do modelo...
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            O modelo está incorporando suas sugestões ao prompt. Quando
            terminar, você verá o diff completo pra revisar antes de salvar.
          </p>
        </div>
      )}

      {/* Banner de erro quando o apply falha (timeout, LLM error, etc) */}
      {applyError && !isStreamingApply && (
        <div
          className="rounded-md px-4 py-3 text-sm flex items-start gap-2"
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
          }}
        >
          <FiAlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-0.5">
              Não foi possível aplicar o treinamento
            </div>
            <div className="text-xs leading-relaxed">{applyError}</div>
          </div>
        </div>
      )}

      {/* CTA principal */}
      <div className="pt-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        <div className="flex items-center justify-between gap-4">
          <div className="text-xs text-slate-500">
            {isStreamingApply
              ? 'Aguarde — o prompt está sendo gerado pelo modelo.'
              : canApply
                ? `${selectedSuggestionIds.length} sugestão(ões) selecionada(s)${userInstructions.trim() ? ' + instruções livres' : ''}`
                : 'Selecione ao menos uma sugestão ou escreva instruções para aplicar.'}
          </div>
          <button
            type="button"
            onClick={onApply}
            disabled={!canApply || applying}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
            style={{
              backgroundColor: canApply && !applying ? '#8b5cf6' : '#cbd5e1',
              color: '#ffffff',
              cursor: canApply && !applying ? 'pointer' : 'not-allowed',
            }}
          >
            {applying && <FiLoader className="h-4 w-4 animate-spin" />}
            {isStreamingApply
              ? 'Gerando...'
              : applyError
                ? 'Tentar novamente'
                : 'Aplicar e ver diff'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-componentes ─────────────────────────────────────────────────────

const MetricsSummary: React.FC<{
  summary: NonNullable<AgentQualitySnapshotResponse['summary']>;
}> = ({ summary }) => {
  const overallPct = Math.round(summary.overallScore * 100);
  const overallColor =
    summary.overallScore >= 0.8
      ? '#10b981'
      : summary.overallScore >= 0.5
        ? '#f59e0b'
        : '#ef4444';

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: '#ffffff',
        borderColor: 'rgba(0, 0, 0, 0.08)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs text-slate-500 uppercase font-semibold tracking-wide">
            Score geral do treino
          </div>
          <div className="text-2xl font-bold" style={{ color: overallColor }}>
            {overallPct}%
          </div>
        </div>
        <div className="text-right text-xs text-slate-500">
          <div>{summary.totalScenarios} cenários executados</div>
          <div>{summary.relevancePassed} aprovados</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {QUALITY_TEST_METRICS.map((key) => {
          const value =
            summary.averages[key as keyof typeof summary.averages] ?? 0;
          const pct = Math.round(value * 100);
          const color =
            value >= 0.8 ? '#10b981' : value >= 0.5 ? '#f59e0b' : '#ef4444';
          const description = getMetricDescription(key);
          return (
            <div
              key={key}
              title={description || undefined}
              className={`rounded px-2 py-1.5 ${description ? 'cursor-help' : ''}`}
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
            >
              <div className="text-xs text-slate-500">
                {getMetricLabel(key)}
              </div>
              <div className="text-sm font-semibold" style={{ color }}>
                {pct}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SuggestionsList: React.FC<{
  suggestions: PromptSuggestion[];
  loading: boolean;
  error: Error | null;
  selectedIds: string[];
  onToggle: (id: string) => void;
  /**
   * Callback pra re-executar o Prompt Coach e receber novas sugestões
   * sem precisar rodar um treino novo. Usado pelo botão "Gerar novas
   * sugestões" (Coach v2 Opção C).
   */
  onRegenerate: () => void;
}> = ({ suggestions, loading, error, selectedIds, onToggle, onRegenerate }) => {
  if (loading) {
    return (
      <div
        className="rounded-lg border p-4 text-sm text-slate-500 flex items-center gap-2"
        style={{
          backgroundColor: '#ffffff',
          borderColor: 'rgba(0, 0, 0, 0.08)',
        }}
      >
        <FiLoader className="h-4 w-4 animate-spin" />
        Analisando resultados e gerando sugestões...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-lg border p-4 text-sm"
        style={{
          backgroundColor: '#fef2f2',
          borderColor: '#fecaca',
          color: '#991b1b',
        }}
      >
        <FiAlertTriangle className="h-4 w-4 inline mr-2" />
        Erro ao gerar sugestões: {error.message}
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div
        className="rounded-lg border p-4 text-sm text-slate-500 flex items-center justify-between gap-2"
        style={{
          backgroundColor: '#ffffff',
          borderColor: 'rgba(0, 0, 0, 0.08)',
        }}
      >
        <span>
          Nenhuma sugestão automática gerada. Use o campo de instruções abaixo
          para fazer ajustes manuais no prompt.
        </span>
        <button
          type="button"
          onClick={onRegenerate}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium flex-shrink-0"
          style={{ color: '#8b5cf6' }}
        >
          <FiRefreshCw className="h-3 w-3" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-800">
          Sugestões de melhoria
        </h3>
        <button
          type="button"
          onClick={onRegenerate}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors"
          style={{ color: '#8b5cf6' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Pede novas sugestões ao Prompt Coach (não consome créditos extras)"
        >
          <FiRefreshCw className="h-3 w-3" />
          Gerar novas sugestões
        </button>
      </div>
      <div className="space-y-2">
        {suggestions.map((s) => (
          <SuggestionCard
            key={s.id}
            suggestion={s}
            selected={selectedIds.includes(s.id)}
            onToggle={() => onToggle(s.id)}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Banner de regressão — aparece quando o backend detectou que o score
 * geral ou um evaluator caiu em relação ao snapshot anterior. Usa
 * gradiente vermelho/laranja pra chamar atenção sem ser agressivo.
 */
const RegressionBanner: React.FC<{
  info: SnapshotRegressionInfo;
}> = ({ info }) => {
  const overallDeltaPct = Math.abs(info.overallScoreDelta * 100).toFixed(1);
  const previousDate = new Date(info.previousSnapshotCreatedAt).toLocaleString(
    'pt-BR',
    { dateStyle: 'short', timeStyle: 'short' },
  );

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: 'rgba(239, 68, 68, 0.04)',
        borderColor: 'rgba(239, 68, 68, 0.25)',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="rounded-full p-2 flex-shrink-0"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
        >
          <FiTrendingDown className="h-4 w-4" style={{ color: '#dc2626' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800 mb-1">
            Este treino piorou em relação ao anterior
          </div>
          <p className="text-xs text-slate-600 leading-relaxed mb-2">
            {info.overallScoreDelta < 0 ? (
              <>
                O score geral caiu{' '}
                <strong style={{ color: '#dc2626' }}>
                  −{overallDeltaPct}%
                </strong>{' '}
                comparado ao último treino ({previousDate}).
              </>
            ) : (
              <>
                Alguns avaliadores específicos regrediram comparado ao último
                treino ({previousDate}).
              </>
            )}{' '}
            Revise as sugestões abaixo antes de aplicar — pode ser que a
            mudança recente tenha quebrado algo.
          </p>
          {info.regressedEvaluators.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {info.regressedEvaluators.map((ev) => (
                <span
                  key={ev.metric}
                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md font-medium"
                  style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    color: '#991b1b',
                  }}
                >
                  {getMetricLabel(ev.metric)}:{' '}
                  {(ev.from * 100).toFixed(0)}% →{' '}
                  <strong>{(ev.to * 100).toFixed(0)}%</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SuggestionCard: React.FC<{
  suggestion: PromptSuggestion;
  selected: boolean;
  onToggle: () => void;
}> = ({ suggestion, selected, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const priorityConfig = PRIORITY_CONFIG[suggestion.priority];

  return (
    <div
      className="rounded-lg border transition-colors"
      style={{
        backgroundColor: selected ? 'rgba(139, 92, 246, 0.04)' : '#ffffff',
        borderColor: selected ? '#8b5cf6' : 'rgba(0, 0, 0, 0.08)',
      }}
    >
      <div className="flex items-start gap-3 p-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-1 h-4 w-4 cursor-pointer accent-violet-500"
          aria-label={`Selecionar sugestão: ${suggestion.title}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="text-sm font-semibold text-slate-800">
              {suggestion.title}
            </h4>
            <span
              className="text-xs px-1.5 py-0.5 rounded font-medium"
              style={{
                backgroundColor: priorityConfig.color,
                color: '#ffffff',
              }}
            >
              {priorityConfig.label}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-2 leading-relaxed">
            {suggestion.description}
          </p>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-xs flex items-center gap-1 font-medium"
            style={{ color: '#8b5cf6' }}
          >
            {expanded ? (
              <>
                <FiChevronUp className="h-3 w-3" />
                Esconder detalhes
              </>
            ) : (
              <>
                <FiChevronDown className="h-3 w-3" />
                Ver detalhes
              </>
            )}
          </button>

          {expanded && (
            <div className="mt-3 space-y-2">
              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1">
                  Mudança proposta
                </div>
                <div
                  className="text-xs rounded px-2 py-1.5 font-mono"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    color: '#475569',
                  }}
                >
                  {suggestion.proposedChange}
                </div>
              </div>

              {suggestion.evidence.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">
                    Evidência nas conversas
                  </div>
                  <ul className="space-y-2">
                    {suggestion.evidence.map((e, idx) => (
                      <li
                        key={idx}
                        className="text-xs rounded px-2 py-1.5 space-y-1"
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                          color: '#64748b',
                        }}
                      >
                        <div className="font-semibold text-slate-700">
                          {e.scenarioId}
                        </div>
                        {e.customerTurns && e.customerTurns.length > 0 ? (
                          <div className="space-y-0.5">
                            {e.customerTurns.map((turn, ti) => (
                              <div key={ti} className="flex gap-1.5">
                                <span
                                  className="font-medium shrink-0"
                                  style={{ color: '#475569' }}
                                >
                                  Cliente:
                                </span>
                                <span className="italic">"{turn}"</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Fallback visível pra snapshots antigos (cache no
                          // React Query / browser) ou backends desatualizados
                          // antes de 2026-05-12 — torna a ausência percebível
                          // em vez de silenciosamente esconder o slot.
                          <div className="flex gap-1.5 opacity-60">
                            <span
                              className="font-medium shrink-0"
                              style={{ color: '#475569' }}
                            >
                              Cliente:
                            </span>
                            <span className="italic">
                              (mensagem do cliente indisponível neste snapshot)
                            </span>
                          </div>
                        )}
                        <div className="flex gap-1.5">
                          <span
                            className="font-medium shrink-0"
                            style={{ color: '#8b5cf6' }}
                          >
                            Agente:
                          </span>
                          <span>{e.snippet}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {suggestion.relatedMetrics.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">
                    Métricas relacionadas
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {suggestion.relatedMetrics.map((m) => {
                      const description = getMetricDescription(m);
                      return (
                        <span
                          key={m}
                          className="text-xs px-1.5 py-0.5 rounded font-medium"
                          style={{
                            backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            color: '#8b5cf6',
                          }}
                          title={description || undefined}
                        >
                          {getMetricLabel(m)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

void FiCheckCircle;
